import { ChannelMediaItem, TodoItem } from "src/services/api/api.types"

/**
 * Returns the YouTube medium-quality thumbnail URL for a given video ID.
 * Uses mqdefault (320×180) which is a true 16:9 crop without letterboxing.
 * hqdefault (480×360) is 4:3 and has black bars baked into the image for 16:9 videos.
 */
export function getYoutubeThumbnailUrl(youtubeId: string): string {
  return `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`
}

/**
 * Extracts the YouTube video ID from common YouTube URL formats:
 *   - https://youtu.be/VIDEO_ID
 *   - https://www.youtube.com/watch?v=VIDEO_ID
 *   - https://www.youtube.com/embed/VIDEO_ID
 * Returns null if the URL is not a recognisable YouTube URL.
 */
export function extractYoutubeId(url: string): string | null {
  if (!url) return null
  // youtu.be short links
  const shortMatch = url.match(/youtu\.be\/([A-Za-z0-9_-]{11})/)
  if (shortMatch) return shortMatch[1]
  // youtube.com/watch?v= and youtube.com/embed/
  const longMatch = url.match(/youtube\.com\/(?:watch\?v=|embed\/)([A-Za-z0-9_-]{11})/)
  if (longMatch) return longMatch[1]
  return null
}

/**
 * Shared ContentLibraryItem type used by both the authenticated (TodoList-backed)
 * and unauthenticated (channel_media API-backed) content library grids.
 */
export interface ContentLibraryItem {
  /** Unique key for this item — task_id (both auth and unauth) */
  id: string
  title: string
  mediaType: "video" | "pdf" | "audio" | "image" | "url" | "youtube" | null
  mediaUrl: string | null
  thumbnailUrl: string | null
  subscriptionOnly: boolean
  sidekickName: string
  /** Padded 2-digit string matching avatarImageMap keys, e.g. "01" */
  sidekickAvatarId: string
  topic?: string
  taskId?: string
  /** The select_esk value for auth CM selection (undefined for unauth items) */
  selectEsk?: string
  /** The first sponsor_id for auth CM selection (undefined for unauth items) */
  sponsorId?: string
  createdAt?: string
  lastPublishedAt?: string | null
  priority?: number | null
  /** Publishing status from the todo item (e.g. "published", "testing", "pending") */
  publishingStatus?: string | null
  /** True when the CM is media-mode — the primary experience is a video/audio player */
  isMediaMode?: boolean
  /** resource_id of the primary source file, used for playback-position tracking */
  sourceResourceId?: string
  /** YouTube video ID (e.g. "LXX_qOA5D8E"), present when mediaType === "youtube" */
  youtubeId?: string | null
}

/**
 * Resolves a potentially relative URL against the API base URL.
 * Absolute URLs (starting with http:// or https://) are returned unchanged.
 * Null/empty values are returned as null.
 */
function resolveUrl(url: string | null | undefined, apiBaseUrl: string | undefined): string | null {
  if (!url) return null
  if (url.startsWith("http://") || url.startsWith("https://")) return url
  if (!apiBaseUrl) return url
  const base = apiBaseUrl.endsWith("/") ? apiBaseUrl.slice(0, -1) : apiBaseUrl
  const path = url.startsWith("/") ? url : `/${url}`
  return `${base}${path}`
}

const DEFAULT_AVATAR_KEY = "00"

// Valid avatar IDs are 2-digit strings from "00" to "43".
// Avoids importing avatarImageMap (which pulls in RiveContext → AuthContext → native modules).
const MAX_AVATAR_ID = 43
function resolveAvatarId(avatarId: string | undefined): string {
  if (!avatarId) return DEFAULT_AVATAR_KEY
  const padded = avatarId.padStart(2, "0")
  const num = parseInt(padded, 10)
  return !isNaN(num) && num >= 0 && num <= MAX_AVATAR_ID ? padded : DEFAULT_AVATAR_KEY
}

/** Convert a raw ChannelMediaItem (from the public /channel_media endpoint) into a ContentLibraryItem. */
export function channelMediaItemToContentLibraryItem(item: ChannelMediaItem): ContentLibraryItem {
  const resolvedAvatarId = resolveAvatarId(item.avatar_id)

  let thumbnailUrl = item.thumbnail_url
  let youtubeId: string | null = null
  if (item.media_type === "youtube" && item.media_url) {
    youtubeId = extractYoutubeId(item.media_url)
    if (!thumbnailUrl && youtubeId) thumbnailUrl = getYoutubeThumbnailUrl(youtubeId)
  }

  return {
    id: item.cm_id ?? `${item.avatar_id}-${item.title}`,
    title: item.title,
    mediaType: item.media_type,
    mediaUrl: item.media_url,
    thumbnailUrl,
    subscriptionOnly: item.subscription_only,
    sidekickName: item.sidekick_name,
    sidekickAvatarId: resolvedAvatarId,
    topic: item.topic,
    taskId: item.task_id,
    createdAt: item.creation_time,
    lastPublishedAt: item.last_published_at,
    priority: item.cm_priority,
    youtubeId,
  }
}

/**
 * Convert a TodoItem (from the authenticated /todo_list endpoint) into a ContentLibraryItem.
 * `avatarId` should be the resolved avatar_id string for the sidekick delivering the item.
 */
export function todoItemToContentLibraryItem(
  item: TodoItem,
  sidekickName: string,
  avatarId?: string,
  apiBaseUrl?: string,
): ContentLibraryItem {
  let thumbnailUrl = resolveUrl(item.thumbnail_url, apiBaseUrl)
  let youtubeId: string | null = null

  if (item.media_type === "youtube") {
    youtubeId =
      item.source_resource?.youtube_id ||
      (item.media_url ? extractYoutubeId(item.media_url) : null) ||
      (item.source_resource?.data ? extractYoutubeId(item.source_resource.data) : null) ||
      null
    if (!thumbnailUrl && youtubeId) thumbnailUrl = getYoutubeThumbnailUrl(youtubeId)
  }

  return {
    id: item.task_id,
    title: item.name,
    mediaType: item.media_type,
    mediaUrl: resolveUrl(item.media_url, apiBaseUrl),
    thumbnailUrl,
    subscriptionOnly: false, // authenticated users have access to their todo items
    sidekickName,
    sidekickAvatarId: resolveAvatarId(avatarId),
    // The auth /todo_list endpoint exposes the display topic as categories[0]
    // (topics[] holds opaque IDs). The unauth /channel_media endpoint pre-flattens
    // this into a single `topic` string — map it here for parity.
    topic: item.categories?.[0],
    taskId: item.task_id,
    selectEsk: item.select_esk,
    sponsorId: item.sponsor_ids?.[0],
    priority: item.topic_priority,
    createdAt: item.created_at,
    lastPublishedAt: item.last_published_at,
    publishingStatus: item.status ?? null,
    isMediaMode: item.is_media_mode,
    sourceResourceId: item.source_resource?.resource_id,
    youtubeId,
  }
}
